/* eslint-disable @next/next/no-img-element */
import { useRouter } from 'next/router'
import { useState, useEffect } from 'react'
import { HiExclamation } from 'react-icons/hi'
import { BsStars } from 'react-icons/bs'
import Meta from 'components/Meta'
import Layout from 'components/Layout'
import Loading from 'components/Loading'
import Result from 'components/mint/Result'
import LedgerModal from 'components/mint/LedgerModal'
import Alert from 'components/Alert'
import useSuggest from 'api/hooks/useSuggest'
import useWallet from 'hooks/useWallet'
import { useStore } from 'store/state/store'
import type { GetServerSideProps } from 'next'

export type MintSearchProps = {
  q: string
  pathname: string
}

export default function MintSearch({ q: searchQuery, pathname }: MintSearchProps) {
  const [searchText, setSearchText] = useState(searchQuery)

  useEffect(() => {
    setSearchText(searchQuery)
  }, [searchQuery])

  const router = useRouter()
  const wallet = useWallet()

  const activeWallet = wallet.getAddress() as string

  const { data, isLoading, error } = useSuggest({
    name: searchQuery,
    params: { view: 'full' },
    options: {
      enabled: searchQuery.length > 0,
      refetchOnWindowFocus: false,
      retry: false
    }
  })

  const warnLedger = useStore((state) => state.warnLedger)
  const hideWarnLedger = useStore((state) => state.hideWarnLedger)

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const filtered = e.target.value.replace(/[^a-z0-9]/gi, '')
    setSearchText(filtered)
  }

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    router.push(`/mint?q=${searchText}`)
  }

  const renderResults = () => {
    if (searchQuery.length === 0) {
      return null
    }

    if (isLoading) {
      return (
        <div className="flex items-center justify-center my-16">
          <Loading />
        </div>
      )
    }

    if (error) {
      return (
        <Alert
          type="error"
          title={`Error fetching search results for "${searchQuery}"`}
          error={error}
        />
      )
    }

    if (!data || data.length === 0) {
      return (
        <Alert type="info" title={`No results`} message={`No results found for "${searchQuery}"`} />
      )
    }

    const results = [...data]
    const match = results.shift()

    return (
      <>
        {match && (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div key={match.name} className="group">
              <Result nfd={match} activeWallet={activeWallet} />
            </div>
          </div>
        )}

        {results.length > 0 && (
          <div className="mt-8 bg-white shadow overflow-hidden sm:rounded-md">
            <ul role="list" className="divide-y divide-gray-200">
              {results.map((result) => (
                <li key={result.name} className="group">
                  <Result nfd={result} activeWallet={activeWallet} />
                </li>
              ))}
            </ul>
          </div>
        )}
      </>
    )
  }

  const renderHeader = () => {
    return (
      <div className="max-w-7xl mx-auto py-8 sm:py-12 md:py-16 px-4 sm:px-6 lg:px-8">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight text-gray-900">
          Mint your NFD
        </h1>

        <form className="mt-4 md:max-w-lg" onSubmit={handleSearch}>
          <div>
            <label htmlFor="mint-search" className="sr-only">
              Search
            </label>
            <div className="mt-1 flex rounded-md shadow-sm">
              <div className="relative flex items-stretch flex-grow focus-within:z-10">
                <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none">
                  <BsStars className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </div>
                <input
                  type="text"
                  name="mint-search"
                  id="mint-search"
                  className="focus:ring-brand-500 focus:border-brand-500 block w-full rounded-none rounded-l-md pl-10 sm:pl-12 sm:py-3 border-gray-300"
                  value={searchText}
                  onChange={handleSearchChange}
                  maxLength={32}
                  autoFocus={true}
                  autoCorrect="off"
                  autoComplete="off"
                  autoCapitalize="off"
                  spellCheck={false}
                />
              </div>
              <button
                type="submit"
                className="-ml-px relative inline-flex items-center space-x-2 px-5 py-2 border border-gray-300 font-medium rounded-r-md text-gray-700 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
              >
                Search
              </button>
            </div>

            {!activeWallet && (
              <div className="mt-4">
                <div className="rounded-md bg-amber-50 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <HiExclamation className="h-5 w-5 text-amber-400" aria-hidden="true" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-amber-700">Wallet not connected</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </form>

        {warnLedger && <LedgerModal onDismiss={hideWarnLedger} />}
      </div>
    )
  }

  return (
    <>
      <Meta title="Mint Search" pathname={pathname} />
      <Layout pathname={pathname}>
        <main className="flex-1 flex flex-col">
          <div className="bg-white border-b border-gray-200">{renderHeader()}</div>

          <section aria-labelledby="mint-results-heading" className="flex-1 bg-gray-50">
            <div className="max-w-xl mx-auto pt-8 pb-16 px-4 sm:pt-16 sm:pb-24 sm:px-6 md:max-w-7xl lg:px-8">
              <h2 id="mint-results-heading" className="sr-only">
                Search results
              </h2>

              {renderResults()}
            </div>
          </section>
        </main>
      </Layout>
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { q = '' } = context.query

  return {
    props: {
      q,
      pathname: context.resolvedUrl
    }
  }
}
